<template>
  <v-autocomplete
    v-model="selectedLoggers"
    :label="$t('loggers')"
    :items="accounts"
    data-testid="logger-complete"
    item-text="name"
    value="name"
    chips
    color="black"
    item-color="primary"
    return-object
    multiple
    :id="elementId"
    :rules="rules"
    :hint="hint"
    :persistent-hint="hint !== ''"
    :menu-props="{
      contentClass: getMenuContentClass
    }"
  >
    <template #append>
      <v-chip v-for="tag in selectedTags" small :key="tag.tagId" close @click:close="tagClicked(tag)">
        {{tag.value}}
      </v-chip>
    </template>
    <template #append-outer>
      <v-icon @click="refreshAccounts" color="primary">mdi-refresh</v-icon>
      <v-menu v-model="showMenu"
      offset-y
      :close-on-content-click="false"
      style="max-width: 600px">
        <template #activator="{on}">
          <v-icon color="primary" v-on="on">{{ tagFilterModeIcon }}</v-icon>
        </template>
        <v-list>
          <TagFilterModeToggle v-model="tagFilterMode" :userSetting="userSetting"/>
          <v-divider/>
          <v-list-item @click="tagClicked(tag)" v-for="tag in accountTags" :key="tag.tagId">
            <v-list-item-title>
              {{tag.value}}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
  </v-autocomplete>
</template>

<script>
import { SETTING_TYPES, SETTING_KEYS } from '@/utils/UserSettings.js'
import { TagFilterMode } from '@/utils/Enumerations.js'
import { accountMatchesTagFilter } from '@/utils/componentHelpers'
import { mapActions, mapGetters, mapMutations } from 'vuex'
export default {
  name: 'LoggerAutocomplete',

  components: {
    TagFilterModeToggle: () => import('@/components/autocomplete/TagFilterModeToggle.vue')
  },

  props: {
    userSetting: {
      type: String,
      default: undefined
    },
    loggers: {
      type: Array
    },
    fetchTagsAndAccounts: {
      type: Boolean,
      default: true
    },
    elementId: {
      type: String,
      required: false,
      default: undefined
    },
    rules: {
      type: Array,
      default: () => []
    },
    hint: {
      type: String,
      default: ''
    }
  },

  data: () => ({
    selectedLoggers: [],
    showMenu: false,
    selectedTags: [],
    tagFilterMode: TagFilterMode.Any.value,
    tags: []
  }),

  watch: {
    selectedLoggers (val) {
      this.$emit('loggers-mutated', val)
    },

    selectedTags (_) {
      this.setUserSettings()
    }
  },

  computed: {
    ...mapGetters('user-settings', ['userSettings']),
    ...mapGetters('account', ['allAccounts', 'accountsLoading']),
    ...mapGetters('tags', ['accountTags']),

    accounts () {
      const accountsFiltered = this.allAccounts.filter(a => accountMatchesTagFilter(a.accountTags, this.selectedTags, this.tagFilterMode))
      return this.loggers
        ? [...new Set([...accountsFiltered, ...this.loggers])]
        : this.allAccounts
    },

    tagFilterModeIcon () {
      return TagFilterMode.forInt(this.tagFilterMode).details.icon
    },

    getMenuContentClass () {
      if (this.elementId) {
        return `${this.elementId}-selection-menu`
      } else {
        return 'selection-menu'
      }
    }
  },

  created () {
    this.getTags()
    this.initializeAccounts()
  },

  methods: {
    ...mapActions('account', ['fetchAccounts', 'getAccountsWithTags']),
    ...mapActions('tags', ['fetchTags']),
    ...mapMutations('user-settings', ['mutateUserSetting']),

    async refreshAccounts () {
      if (!this.accountsLoading) await this.fetchAccounts()
    },

    async getTags () {
      if (this.fetchTagsAndAccounts) await this.fetchTags(0)
      this.checkUserSettings()
    },

    async initializeAccounts () {
      if (this.fetchTagsAndAccounts) await this.fetchAccounts()
    },

    tagClicked (tag) {
      const tagIndex = this.selectedTags.findIndex(t => t.tagId === tag.tagId)
      if (tagIndex === -1) {
        this.selectedTags.push(tag)
      } else {
        this.selectedTags.splice(tagIndex, 1)
      }
    },

    checkUserSettings () {
      if (this.userSetting === 'accountLoggerAutocomplete') {
        this.selectedTags = this.tagsFromIds(this.userSettings[SETTING_TYPES.TRACT_FORM][SETTING_KEYS.ACCOUNT_AUTOCOMPLETE])
        this.tagFilterMode = this.userSettings[SETTING_TYPES.AUTOCOMPLETE][SETTING_KEYS.ACCOUNT_AUTOCOMPLETE_TAG_FILTER_MODES][this.userSetting] ?? TagFilterMode.Any.value
        if (this.loggers) {
          this.selectedLoggers = this.loggers
        } else {
          this.refreshAccounts()
        }
      } else {
        this.refreshAccounts()
      }
    },

    setUserSettings () {
      this.mutateUserSetting({
        type: SETTING_TYPES.TRACT_FORM,
        key: SETTING_KEYS.ACCOUNT_AUTOCOMPLETE,
        value: this.selectedTags.map(t => t.tagId)
      })
    },

    tagsFromIds (ids) {
      return (ids ?? []).map(id => this.accountTags.find(t => t.tagId === id)).filter(t => t !== undefined)
    }
  }
}
</script>
